function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useEffect, useState, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { spacing, breakpoints, themeLight, icons } from '@naf/theme';
import { Text, TextVariant } from '@naf/text';
import { ArrowDownward } from '@styled-icons/material/ArrowDownward';
import { ArrowBack } from '@styled-icons/material/ArrowBack';
import { ArrowForward } from '@styled-icons/material/ArrowForward';
import { Launch } from '@styled-icons/material/Launch';
import { Map } from '@styled-icons/material/Map';
export const ButtonLink = /*#__PURE__*/forwardRef(({
  href,
  onClick,
  target,
  disabled = false,
  variant = 'primary',
  text,
  icon,
  prefixIcon = false,
  size,
  className,
  ...rest
}, ref) => {
  const [mouseDown, setMouseDown] = useState(false);
  const removeOutline = () => {
    setMouseDown(true);
  };
  const addOutline = e => {
    if (e.key === 'Tab') {
      setMouseDown(false);
    }
  };
  const renderIcon = icon => {
    switch (icon) {
      case 'arrow-back':
        return /*#__PURE__*/React.createElement(ArrowBack, {
          size: 16
        });
      case 'arrow-forward':
        return /*#__PURE__*/React.createElement(ArrowForward, {
          size: 16
        });
      case 'arrow-down':
        return /*#__PURE__*/React.createElement(ArrowDownward, {
          size: 16
        });
      case 'launch':
        return /*#__PURE__*/React.createElement(Launch, {
          size: 16
        });
      case 'map':
        return /*#__PURE__*/React.createElement(Map, {
          size: 16
        });
      default:
        return null;
    }
  };
  const fontSize = size => {
    switch (size) {
      case 'small':
        return TextVariant.ButtonSmall;
      case 'standard':
        return TextVariant.Button;
      default:
        return TextVariant.Button;
    }
  };
  useEffect(() => {
    window.addEventListener('mousedown', removeOutline);
    return () => window.removeEventListener('mousedown', removeOutline);
  }, []);
  useEffect(() => {
    window.addEventListener('keydown', addOutline);
    return () => window.removeEventListener('keydown', addOutline);
  }, []);
  return /*#__PURE__*/React.createElement(Container, {
    $prefixIcon: prefixIcon,
    $disabled: disabled,
    $variant: variant,
    className: className
  }, /*#__PURE__*/React.createElement(Link, _extends({
    href: disabled ? '' : href,
    onClick: () => {
      if (!disabled) {
        onClick && onClick();
      }
    },
    tabIndex: disabled ? -1 : 0,
    target: target,
    id: mouseDown ? 'mousedown' : '',
    "aria-disabled": disabled,
    ref: ref
  }, rest), /*#__PURE__*/React.createElement(Text, {
    variant: fontSize(size)
  }, text), icon && renderIcon(icon)));
});
export default ButtonLink;
const Container = styled.div.withConfig({
  displayName: "button-link__Container",
  componentId: "sc-10a6lnd-0"
})(["display:flex;align-items:center;position:relative;width:fit-content;height:44px;color:", ";&:hover{cursor:pointer;}&:hover,&:focus{a > span{border-color:transparent;}a > span,svg{color:", ";}", "}", " ", " ", " ", " ", " svg{position:relative;left:", ";padding-bottom:", ";font-size:", ";}", ""], ({
  theme
}) => theme.typography?.defaultText ?? themeLight.typography.defaultText, ({
  theme
}) => theme.typography?.textLink ?? themeLight.typography.textLink, ({
  $variant
}) => $variant === 'inverted' && css(["a > span,svg{color:", ";border-color:transparent;}"], ({
  theme
}) => theme.typography?.textLinkInverted ?? themeLight.typography.textLinkInverted), ({
  $variant
}) => $variant === 'primary' && css(["a > span,a:visited > span{border-color:", ";}"], ({
  theme
}) => theme.typography?.textLink ?? themeLight.typography.textLink), ({
  $variant
}) => $variant === 'secondary' && css(["a > span,a:visited > span{border-color:", ";}"], ({
  theme
}) => theme.typography?.subtleText ?? themeLight.typography.subtleText), ({
  $variant
}) => $variant === 'inverted' && css(["svg,a > span,a:visited > span{color:", ";border-color:", ";}"], ({
  theme
}) => theme.typography?.defaultTextInverted ?? themeLight.typography.defaultTextInverted, ({
  theme
}) => theme.typography?.textLinkInverted ?? themeLight.typography.textLinkInverted), ({
  $disabled
}) => $disabled && css(["pointer-events:none;svg,a > span,a:visited > span{color:", ";border-color:", ";}"], ({
  theme
}) => theme.typography?.disabledText ?? themeLight.typography.disabledText, ({
  theme
}) => theme.typography?.disabledText ?? themeLight.typography.disabledText), ({
  $variant,
  $disabled
}) => $variant === 'inverted' && $disabled && css(["svg,a > span,a:visited > span{color:", ";border-color:", ";}"], ({
  theme
}) => theme.typography?.disabledTextInverted ?? themeLight.typography.disabledTextInverted, ({
  theme
}) => theme.typography?.disabledTextInverted ?? themeLight.typography.disabledTextInverted), spacing.space8, spacing.space8, icons.s, ({
  $prefixIcon
}) => $prefixIcon && css(["svg{position:absolute;left:0;}a{margin-left:", ";}"], spacing.space24));
const Link = styled.a.withConfig({
  displayName: "button-link__Link",
  componentId: "sc-10a6lnd-1"
})(["text-decoration:none;display:flex;align-items:center;> span{padding:0 0 ", " 0;border-bottom:2px solid;border-color:", ";}@media (max-width:", "){font-size:1rem;line-height:1rem;padding-bottom:", ";}&#mousedown{outline:0;}"], spacing.space4, ({
  theme
}) => theme.typography?.textLink ?? themeLight.typography.textLink, breakpoints.s, spacing.space4);
//# sourceMappingURL=button-link.js.map